// Pure logic for the Tuesday Call Sheet. No React. No Firestore.
//
// A call-sheet "week" runs Tuesday through the following Monday and is keyed
// by its Tuesday as a local YYYY-MM-DD string. All date math is LOCAL time —
// toISOString() would shift evening dates to the next UTC day.

import { buildClientName } from "./clientNames";
import { normalizeContact } from "./contactNormalize";
import { creditedAgentId, dealCreditsAgent } from "./dealCredit";

export const CALL_SHEET_STAGES = [
  "Pre-Market",
  "Coming Soon",
  "Active",
  "Under Contract",
  "Pending",
];

// Closest-to-closing first; recently-closed entries always sort after these.
const STAGE_ORDER = {
  Pending: 0,
  "Under Contract": 1,
  Active: 2,
  "Coming Soon": 3,
  "Pre-Market": 4,
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

export function localIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function tuesdayOf(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (d.getDay() - 2 + 7) % 7; // days since this week's Tuesday
  d.setDate(d.getDate() - offset);
  return d;
}

export function weekKeyFor(date) {
  return localIsoDate(tuesdayOf(date));
}

export function prevWeekKeyFor(date) {
  const t = tuesdayOf(date);
  t.setDate(t.getDate() - 7);
  return localIsoDate(t);
}

export function preMarketQualifies(deal, now) {
  if (deal?.dealStage !== "Pre-Market") return true;
  const s = String(deal?.listingAppointment || "").trim();
  const m = ISO_DATE.exec(s);
  if (!m) return true; // unset or malformed: include, never silently skip
  // Exclude only FUTURE-year targets (next-year sellers wait for their year);
  // past and current years are included.
  return Number(m[1]) <= now.getFullYear();
}

export function closedLastMonth(deal, now) {
  if (deal?.dealStage !== "Closed") return false;
  const m = ISO_DATE.exec(String(deal?.closingdate || "").trim());
  if (!m) return false;
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return Number(m[1]) === prev.getFullYear() && Number(m[2]) - 1 === prev.getMonth();
}

export function shouldAutoOpen(sheet) {
  return !!sheet && !sheet.popupShownAt;
}

function firstPhone(dealRecord, contactsById) {
  const list = Array.isArray(dealRecord?.customers) ? dealRecord.customers : [];
  for (const c of list) {
    const contactId = typeof c === "string" ? c : c && c.contactId;
    if (contactId && contactsById?.[contactId]) {
      const phone = normalizeContact(contactsById[contactId]).phone;
      if (phone) return phone;
    }
    if (c && typeof c === "object" && c.phone) return String(c.phone);
  }
  return "";
}

const dealKey = (d) => String(d?.id || d?.createdat || "");

// agentId scopes the sheet to one person's own clients. It defers to
// dealCreditsAgent so the sheet always agrees with the roster about who owns a
// deal (in-house referrals credit the team lead, not the agent on the deal).
// Omit agentId for the unscoped, team-wide sheet.
//
// The one place scoping on credit alone could hurt: a deal that names NO agent
// and NO team lead credits nobody, so it would match nobody's sheet and the
// client would silently drop off the call list entirely. There are no such
// deals in the calling stages today, so this is a safety net rather than a
// live repair, but a client is never allowed to vanish because a field was
// left blank. An unowned deal shows on every scoped sheet, which is the safe
// direction to err: somebody calls them.
const onSheetFor = (deal, agentId) =>
  !agentId || dealCreditsAgent(deal, agentId) || !creditedAgentId(deal);

// A week's sheet self-heals on open: membership and display fields are
// REBUILT from today's deals, then everything the owner DID this week —
// check-offs, notes, drag order — carries over from the stored entries.
// Two live cases drove this (Henry, 2026-09-01): stages moved mid-week but
// the chips stayed frozen ("wy does this say under contact when they are
// pending"), and a deal whose agent was corrected after Tuesday stayed on
// the wrong person's sheet all week (Paul / 2245 Highland — David's listing
// on Henry's list). Rebuilding also lets a deal that ENTERS a calling stage
// join mid-week instead of waiting for next Tuesday.
// Names/phones fall back to the stored copy when the rebuild ran without
// contact data (the caller only fetches contacts when a new deal joined).
export function mergeWeekProgress(rebuilt, priorEntries) {
  const priorById = new Map(
    (priorEntries || []).map((e) => [String(e.dealId), e]),
  );
  return (rebuilt || []).map((e) => {
    const prior = priorById.get(String(e.dealId));
    if (!prior) return e;
    return {
      ...e,
      clientNames: e.clientNames || prior.clientNames || "",
      phone: e.phone || prior.phone || "",
      calledAt: prior.calledAt ?? null,
      note: prior.note || "",
      noteLine: prior.noteLine || "",
      noteUpdatedAt: prior.noteUpdatedAt ?? null,
      order: prior.order ?? e.order,
    };
  });
}

export function buildCallSheetEntries(deals, prevEntries, contactsById, now, agentId) {
  const live = (Array.isArray(deals) ? deals : []).filter(
    (d) =>
      !(typeof d?.deletedAt === "number" && d.deletedAt > 0) &&
      onSheetFor(d, agentId),
  );
  const current = live.filter(
    (d) => CALL_SHEET_STAGES.includes(d?.dealStage) && preMarketQualifies(d, now),
  );
  const recent = live.filter((d) => closedLastMonth(d, now));
  const missedIds = new Set(
    (prevEntries || []).filter((e) => !e.calledAt).map((e) => String(e.dealId)),
  );

  const toEntry = (d, section) => ({
    dealId: dealKey(d),
    section,
    clientNames: buildClientName(d, contactsById) || "",
    address: d.propertyaddress || "",
    phone: firstPhone(d, contactsById),
    stage: d.dealStage || "",
    order: 0,
    missedLastWeek: missedIds.has(dealKey(d)),
    calledAt: null,
    note: "",
    noteLine: "",
    noteUpdatedAt: null,
  });

  const byStageThenAddress = (a, b) =>
    (STAGE_ORDER[a.dealStage] ?? 9) - (STAGE_ORDER[b.dealStage] ?? 9) ||
    String(a.propertyaddress || "").localeCompare(String(b.propertyaddress || ""));
  const byClosing = (a, b) =>
    String(a.closingdate || "").localeCompare(String(b.closingdate || ""));

  const entries = [
    ...[...current].sort(byStageThenAddress).map((d) => toEntry(d, "current")),
    ...[...recent].sort(byClosing).map((d) => toEntry(d, "recentlyClosed")),
  ];
  entries.forEach((e, i) => {
    e.order = i;
  });
  return entries;
}

// ---- Working the list top to bottom ---------------------------------------
// Once a call is made it is only in the way, so checked-off clients drop to the
// bottom under "Already called" and the remaining calls close ranks above.
//
// `order` is Henry's hand-picked calling sequence for the week and is the only
// thing that decides position. Checking a row off deliberately does NOT touch
// it, which is what lets an accidental tick be undone: the row returns to the
// exact slot it came from rather than the end of the queue.
const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

export function splitCallList(entries) {
  const list = [...(entries || [])].sort(byOrder);
  return {
    toCall: list.filter((e) => !e.calledAt),
    alreadyCalled: list.filter((e) => e.calledAt),
  };
}

// Move one still-to-call row in front of another. Only the rows in the calling
// list take part: they swap around among the `order` values they already
// occupy between them, so every checked-off row keeps the exact order it had
// and stays put when it is later unchecked.
export function reorderToCall(entries, fromDealId, toDealId) {
  if (String(fromDealId) === String(toDealId)) return entries;

  const { toCall } = splitCallList(entries);
  const from = toCall.findIndex((e) => String(e.dealId) === String(fromDealId));
  const to = toCall.findIndex((e) => String(e.dealId) === String(toDealId));
  // Either end of the drag not being a call still to make (a checked-off row,
  // or a stale id) means there is nothing sensible to do.
  if (from < 0 || to < 0) return entries;

  const moved = [...toCall];
  const [row] = moved.splice(from, 1);
  moved.splice(to, 0, row);

  // The slots these rows already hold, handed back out in the new sequence.
  const slots = toCall.map((e) => e.order || 0).sort((a, b) => a - b);
  const reassigned = new Map(
    moved.map((e, i) => [String(e.dealId), slots[i]]),
  );

  return (entries || []).map((e) =>
    reassigned.has(String(e.dealId))
      ? { ...e, order: reassigned.get(String(e.dealId)) }
      : e,
  );
}
